import React, { useEffect, useRef, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { CELL_SAVE_DEBOUNCE_MS } from '../../config';
import { notifyReadOnly } from '../../../../utils/supabaseErrors';

interface AggregateFeeCellProps {
  aggregateRowId: number;
  fyStartYear: number;
  totalFee: number;
  onFeeSaved: (newAmount: number) => void;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Editable total-fee input for an aggregate row. Sets the FY budget that the
 * unallocated column subtracts the monthly targets from. Same debounce-on-
 * change + save-on-blur pattern as MonthlyCell / AggregateMonthlyCell, but
 * keyed on (aggregate_row, fy_start_year).
 */
const AggregateFeeCell: React.FC<AggregateFeeCellProps> = ({
  aggregateRowId,
  fyStartYear,
  totalFee,
  onFeeSaved,
}) => {
  const supabase = useSupabaseClient();
  const [draft, setDraft] = useState<string>(totalFee > 0 ? String(totalFee) : '');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedValue = useRef<number>(totalFee);

  // Sync draft from the upstream totalFee only on EXTERNAL changes. After our
  // own save the parent echoes back the value we just persisted — skipping
  // it here prevents in-flight keystrokes from being wiped.
  useEffect(() => {
    if (totalFee === lastSavedValue.current) return;
    setDraft(totalFee > 0 ? String(totalFee) : '');
    lastSavedValue.current = totalFee;
  }, [totalFee]);

  const persist = async (rawValue: string) => {
    const parsed = rawValue.trim() === '' ? 0 : parseFloat(rawValue);
    if (isNaN(parsed)) {
      setSaveState('error');
      return;
    }
    if (parsed === lastSavedValue.current) return;

    setSaveState('saving');
    const { error } = await supabase.rpc('upsert_aggregate_annual_fee', {
      p_aggregate_row_id: aggregateRowId,
      p_fy_start_year: fyStartYear,
      p_amount: parsed,
    });

    if (error) {
      console.error('Aggregate annual fee save error:', error);
      notifyReadOnly(error);
      setSaveState('error');
      return;
    }

    lastSavedValue.current = parsed;
    setSaveState('saved');
    onFeeSaved(parsed);
    setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 1200);
  };

  const handleChange = (value: string) => {
    setDraft(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => persist(value), CELL_SAVE_DEBOUNCE_MS);
  };

  const handleBlur = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    persist(draft);
  };

  return (
    <div className="aggregate-fee-cell">
      <input
        type="number"
        inputMode="decimal"
        step="0.01"
        className="aggregate-fee-cell__input"
        value={draft}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        placeholder="—"
        title="Annual budget for this aggregate row"
      />
      {saveState === 'saving' && <span className="aggregate-fee-cell__indicator aggregate-fee-cell__indicator--saving">…</span>}
      {saveState === 'saved' && <span className="aggregate-fee-cell__indicator aggregate-fee-cell__indicator--saved">✓</span>}
      {saveState === 'error' && <span className="aggregate-fee-cell__indicator aggregate-fee-cell__indicator--error">!</span>}
    </div>
  );
};

export default AggregateFeeCell;
